import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, FileText, ChevronDown, UploadCloud as CloudUpload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { type ProductData } from './ConfirmationPage';
import { type Product } from '@/service/returnService';
import { saveQrDraft } from '@/utils/qrCache';

interface ProductDetailsFormProps {
    onClose: () => void;
    onConfirm: (data: ProductData) => void;
    initialData?: ProductData | null;
    products?: Product[];
    merchantName?: string;
    merchantId?: string;
}

// Generate a short order ID
const generateOrderId = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 7; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

const ProductThumbnail: React.FC<{ src: string | null; alt: string }> = ({ src, alt }) => {
    const [hasError, setHasError] = useState(false);

    if (!src || hasError) {
        return (
            <div className="tw-w-8 tw-h-8 tw-bg-gray-100 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <ImageIcon className="tw-w-4 tw-h-4 tw-text-gray-400" strokeWidth={1.5} />
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            className="tw-w-8 tw-h-8 tw-object-cover tw-rounded-md tw-bg-gray-100 tw-flex-shrink-0"
            onError={() => setHasError(true)}
        />
    );
};

const ProductDetailsForm: React.FC<ProductDetailsFormProps> = ({ onClose, onConfirm, initialData, products = [], merchantName = "Merchant", merchantId }) => {
    const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(
        products.find(p => p.title === initialData?.productName)
    );
    const [productName, setProductName] = useState(initialData?.productName || '');
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [productPhoto, setProductPhoto] = useState<File | null>(initialData?.productPhoto || null);
    const [purchaseInvoice, setPurchaseInvoice] = useState<File | null>(initialData?.purchaseInvoice || null);
    const [photoDragActive, setPhotoDragActive] = useState(false);
    const [invoiceDragActive, setInvoiceDragActive] = useState(false);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [isUploadingInvoice, setIsUploadingInvoice] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orderId] = useState(initialData?.orderId || generateOrderId());

    const dropdownRef = useRef<HTMLDivElement>(null);
    const photoInputRef = useRef<HTMLInputElement>(null);
    const invoiceInputRef = useRef<HTMLInputElement>(null);

    const productOptions = products.length > 0
        ? products
        : [];

    const filteredOptions = productOptions.filter(option => {
        const name = typeof option === 'string' ? option : option.title;
        return name.toLowerCase().includes(productName.toLowerCase());
    });

    const handleSelectProduct = (product: Product | string) => {
        if (typeof product === 'string') {
            setProductName(product);
            setSelectedProduct(undefined);
        } else {
            setProductName(product.title);
            setSelectedProduct(product);
        }
        setIsDropdownOpen(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const simulateUpload = async (file: File, setter: (f: File) => void, loadingSetter: (l: boolean) => void) => {
        loadingSetter(true);
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        setter(file);
        loadingSetter(false);
    };

    // --- Drag & Drop / File Select helpers ---
    const handleDrag = useCallback((e: React.DragEvent, setActive: (v: boolean) => void) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setActive(true);
        } else if (e.type === 'dragleave') {
            setActive(false);
        }
    }, []);

    const handlePhotoDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setPhotoDragActive(false);
        const file = e.dataTransfer.files?.[0];
        if (file && ['image/jpeg', 'image/png'].includes(file.type)) {
            simulateUpload(file, setProductPhoto, setIsUploadingPhoto);
        }
    }, []);

    const handleInvoiceDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setInvoiceDragActive(false);
        const file = e.dataTransfer.files?.[0];
        if (file && ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
            simulateUpload(file, setPurchaseInvoice, setIsUploadingInvoice);
        }
    }, []);

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            simulateUpload(file, setProductPhoto, setIsUploadingPhoto);
        }
    };

    const handleInvoiceSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            simulateUpload(file, setPurchaseInvoice, setIsUploadingInvoice);
        }
    };

    const handleFormConfirm = async () => {
        setIsSubmitting(true);
        const dataToSave = {
            productName,
            id: selectedProduct?.id,
            price: selectedProduct?.price,
            orderId,
            productPhoto,
            purchaseInvoice,
        };
        if (merchantId) {
            await saveQrDraft(merchantId, dataToSave);
        } else {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        onConfirm(dataToSave);
    };

    // Auto-save draft changes to cache
    useEffect(() => {
        if (!merchantId) return;
        const timeoutId = setTimeout(() => {
            saveQrDraft(merchantId, {
                productName,
                id: selectedProduct?.id,
                price: selectedProduct?.price,
                orderId,
                productPhoto,
                purchaseInvoice
            });
        }, 1000); // 1-second debounce
        return () => clearTimeout(timeoutId);
    }, [productName, selectedProduct, orderId, productPhoto, purchaseInvoice, merchantId]);

    return (
        <div className="tw-h-full tw-flex tw-flex-col">
            <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-8 sm:tw-px-4">

                {/* Header */}
                <div className="tw-text-center tw-mb-2">
                    <h1 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
                        Let's verify your product
                    </h1>
                    <p className="tw-text-gray-500 tw-text-sm sm:tw-text-base">
                        This helps {merchantName} review your request faster.
                    </p>
                </div>

                {/* Product Name Section */}
                <div className="tw-space-y-2" ref={dropdownRef}>
                    <Label className="tw-text-gray-900 tw-font-semibold tw-text-base">Product Name</Label>

                    <div className="tw-relative">
                        <input
                            type="text"
                            placeholder="Start typing the product name"
                            value={productName}
                            onChange={(e) => {
                                setProductName(e.target.value);
                                const exactMatch = products.find(p => p.title.toLowerCase() === e.target.value.toLowerCase());
                                if (exactMatch) {
                                    setSelectedProduct(exactMatch);
                                } else {
                                    setSelectedProduct(undefined);
                                }
                                if (!isDropdownOpen) setIsDropdownOpen(true);
                            }}
                            onFocus={() => setIsDropdownOpen(true)}
                            className="tw-w-full tw-h-12 tw-px-4 tw-pr-10 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-text-sm tw-text-gray-900 tw-placeholder-gray-400 tw-outline-none focus:tw-border-gray-300 focus:tw-ring-0"
                        />
                        <button
                            type="button"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-outline-none tw-bg-transparent tw-border-none tw-cursor-pointer"
                        >
                            <ChevronDown className={`tw-w-5 tw-h-5 tw-transition-transform tw-duration-200 ${isDropdownOpen ? 'tw-rotate-180' : ''}`} />
                        </button>

                        {/* Dropdown options — absolutely positioned, single outer border */}
                        {isDropdownOpen && filteredOptions.length > 0 && (
                            <div className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-z-50 tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-shadow-lg tw-max-h-[200px] tw-overflow-y-auto">
                                {filteredOptions.map((option, index) => {
                                    const name = typeof option === 'string' ? option : option.title;
                                    const image = typeof option === 'string' ? null : option.image_url;

                                    return (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => handleSelectProduct(option)}
                                            className={`tw-w-full tw-text-left tw-px-4 tw-py-3.5 tw-bg-white tw-text-sm tw-text-gray-900 tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors tw-outline-none tw-border-0
                                                ${index > 0 ? 'tw-border-t tw-border-gray-100' : ''}
                                                tw-flex tw-items-center tw-gap-3
                                            `}
                                            style={index > 0 ? { borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: '#f3f4f6' } : {}}
                                        >
                                            <ProductThumbnail src={image} alt={name} />
                                            <span className="tw-line-clamp-2 tw-overflow-hidden tw-text-ellipsis tw-break-words">{name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {isDropdownOpen && filteredOptions.length === 0 && (
                            <div className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-z-50 tw-mt-1 tw-px-4 tw-py-3 tw-text-sm tw-text-gray-400 tw-italic tw-border tw-border-gray-200 tw-rounded-xl tw-bg-white tw-shadow-lg">
                                No matches found
                            </div>
                        )}
                    </div>
                </div>

                {/* Product Photo Upload */}
                <div className="tw-space-y-3">
                    <div className="tw-flex tw-items-start tw-gap-2.5">
                        <Camera className="tw-w-6 tw-h-6 tw-text-gray-700 tw-mt-0.5" strokeWidth={1.5} />
                        <div>
                            <Label className="tw-text-lg tw-font-bold tw-text-gray-900">
                                Product photo<span className="tw-text-red-500">*</span>
                            </Label>
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-0">Accepted formats: JPG, PNG</p>
                        </div>
                    </div>

                    {/* Hidden file input */}
                    <input
                        ref={photoInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={handlePhotoSelect}
                        className="tw-hidden"
                    />

                    {isUploadingPhoto ? (
                        <div className="tw-w-full tw-py-8 tw-border-2 tw-border-dashed tw-border-gray-300 tw-bg-gray-50 tw-rounded-2xl tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3">
                            <div className="tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center">
                                <div className="tw-w-6 tw-h-6 tw-border-2 tw-border-cyan-400 tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
                            </div>
                            <span className="tw-text-gray-500 tw-text-sm tw-font-medium">Uploading...</span>
                        </div>
                    ) : productPhoto ? (
                        <div className="tw-w-full tw-p-3 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl tw-flex tw-items-center tw-justify-between">
                            <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                <CloudUpload className="tw-w-5 tw-h-5 tw-text-cyan-400 tw-flex-shrink-0" strokeWidth={2} />
                                <span className="tw-text-sm tw-text-gray-700 tw-truncate">{productPhoto.name}</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setProductPhoto(null); if (photoInputRef.current) photoInputRef.current.value = ''; }}
                                className="tw-p-1 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none tw-flex-shrink-0"
                            >
                                <X className="tw-w-4 tw-h-4" />
                            </button>
                        </div>
                    ) : (
                        <div
                            onDragEnter={(e) => handleDrag(e, setPhotoDragActive)}
                            onDragLeave={(e) => handleDrag(e, setPhotoDragActive)}
                            onDragOver={(e) => handleDrag(e, setPhotoDragActive)}
                            onDrop={handlePhotoDrop}
                            onClick={() => photoInputRef.current?.click()}
                            className={`tw-w-full tw-py-8 tw-border-2 tw-border-dashed tw-rounded-2xl tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-cursor-pointer tw-transition-colors
                                ${photoDragActive
                                    ? 'tw-border-cyan-500 tw-bg-cyan-50'
                                    : 'tw-border-cyan-300 tw-bg-gray-50 hover:tw-bg-cyan-50 hover:tw-border-cyan-400'
                                }`}
                        >
                            <div className="tw-w-10 tw-h-10 tw-bg-cyan-400 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
                                <CloudUpload className="tw-w-5 tw-h-5 tw-text-white" strokeWidth={2} />
                            </div>
                            <span className="tw-text-gray-900 tw-font-bold tw-text-sm tw-tracking-wide tw-uppercase">Drop file here</span>
                            <span className="tw-text-gray-400 tw-text-xs tw-text-center tw-leading-relaxed">Drag and drop your PNG, JPG<br />images here.</span>
                        </div>
                    )}
                </div>

                {/* Purchase Invoice Upload */}
                <div className="tw-space-y-3">
                    <div className="tw-flex tw-items-start tw-gap-2.5">
                        <FileText className="tw-w-6 tw-h-6 tw-text-gray-700 tw-mt-0.5" strokeWidth={1.5} />
                        <div>
                            <Label className="tw-text-lg tw-font-bold tw-text-gray-900">
                                Purchase invoice<span className="tw-text-red-500">*</span>
                            </Label>
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-0">Accepted formats: PDF, JPG, PNG</p>
                        </div>
                    </div>

                    {/* Hidden file input */}
                    <input
                        ref={invoiceInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleInvoiceSelect}
                        className="tw-hidden"
                    />

                    {isUploadingInvoice ? (
                        <div className="tw-w-full tw-py-8 tw-border-2 tw-border-dashed tw-border-gray-300 tw-bg-gray-50 tw-rounded-2xl tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3">
                            <div className="tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center">
                                <div className="tw-w-6 tw-h-6 tw-border-2 tw-border-cyan-400 tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
                            </div>
                            <span className="tw-text-gray-500 tw-text-sm tw-font-medium">Uploading...</span>
                        </div>
                    ) : purchaseInvoice ? (
                        <div className="tw-w-full tw-p-3 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl tw-flex tw-items-center tw-justify-between">
                            <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                <CloudUpload className="tw-w-5 tw-h-5 tw-text-cyan-400 tw-flex-shrink-0" strokeWidth={2} />
                                <span className="tw-text-sm tw-text-gray-700 tw-truncate">{purchaseInvoice.name}</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setPurchaseInvoice(null); if (invoiceInputRef.current) invoiceInputRef.current.value = ''; }}
                                className="tw-p-1 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none tw-flex-shrink-0"
                            >
                                <X className="tw-w-4 tw-h-4" />
                            </button>
                        </div>
                    ) : (
                        <div
                            onDragEnter={(e) => handleDrag(e, setInvoiceDragActive)}
                            onDragLeave={(e) => handleDrag(e, setInvoiceDragActive)}
                            onDragOver={(e) => handleDrag(e, setInvoiceDragActive)}
                            onDrop={handleInvoiceDrop}
                            onClick={() => invoiceInputRef.current?.click()}
                            className={`tw-w-full tw-py-8 tw-border-2 tw-border-dashed tw-rounded-2xl tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-cursor-pointer tw-transition-colors
                                ${invoiceDragActive
                                    ? 'tw-border-cyan-500 tw-bg-cyan-50'
                                    : 'tw-border-cyan-300 tw-bg-gray-50 hover:tw-bg-cyan-50 hover:tw-border-cyan-400'
                                }`}
                        >
                            <div className="tw-w-10 tw-h-10 tw-bg-cyan-400 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
                                <CloudUpload className="tw-w-5 tw-h-5 tw-text-white" strokeWidth={2} />
                            </div>
                            <span className="tw-text-gray-900 tw-font-bold tw-text-sm tw-tracking-wide tw-uppercase">Drop file here</span>
                            <span className="tw-text-gray-400 tw-text-xs tw-text-center tw-leading-relaxed">Drag and drop your PDF, JPG,<br />or PNG files here.</span>
                        </div>
                    )}
                </div>

                {/* Confirmation Checkbox — using Radix Checkbox */}
                <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2">
                    <Checkbox
                        id="confirm-details"
                        checked={isConfirmed}
                        onCheckedChange={(checked) => setIsConfirmed(checked === true)}
                        className="tw-h-4 tw-w-4 tw-mt-0.5 tw-border-gray-300 data-[state=checked]:tw-bg-cyan-400 data-[state=checked]:tw-border-cyan-400"
                    />
                    <Label
                        htmlFor="confirm-details"
                        className="tw-font-medium tw-text-sm tw-text-gray-700 tw-cursor-pointer tw-leading-5"
                    >
                        I confirm the details shared are correct
                    </Label>
                </div>

                {/* Submit Button */}
                <div className="tw-mt-auto tw-pb-4 sm:tw-pb-0">
                    <Button
                        onClick={handleFormConfirm}
                        disabled={!isConfirmed || !productName || !productPhoto || !purchaseInvoice || isSubmitting}
                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-12 tw-bg-cyan-50 hover:tw-bg-cyan-100 tw-text-cyan-600 disabled:tw-text-gray-400 disabled:tw-bg-gray-100 tw-font-semibold tw-text-lg tw-rounded-lg tw-border-none tw-transition-colors"
                    >
                        {isSubmitting && <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin" />}
                        Confirm product
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsForm;
